import {from, mergeMap, Subject} from "rxjs";
import {promiseMedResolvere} from "../støttefunksjoner/promiser";
import {map} from "rxjs/operators";
import {nyUuid} from "../støttefunksjoner/uuidGenerator";
import {initDebugger} from "../støttefunksjoner/debug";
import {Signal, signal} from "@angular/core";

const debug = initDebugger('Arbeidskø');

export class Arbeidskø<T> {
    private arbeid = new Subject<ArbeidIntern<T>>();
    private kødybdeSignal = signal(0);

    constructor(private readonly antallParalleller = 10) {
    }

    register(arbeid: (arbeidId: string) => void): PlanlagtArbeid<T> {
        const prom = promiseMedResolvere<T>()
        const asynkArbeid:Arbeid<T> = arbeidId => {
            arbeid(arbeidId);
            return prom.promise
        };
        const planlagtArbeid = this.registerAsynkront(asynkArbeid);
        return {
            id: planlagtArbeid.id,
            avslutt: (resultat: T) => prom.resolve(resultat)
        }
    }

    registerAsynkront(arbeid: Arbeid<T>): PlanlagtAsynkrontArbeid<T> {
        this.kødybdeSignal.update(dybde => dybde + 1);
        const arbeidIntern = new ArbeidIntern<T>(arbeid);
        this.arbeid.next(arbeidIntern);
        return arbeidIntern.somPlanlagtAsynkrontArbeid();
    }

    startKonsumering() {
        this.arbeid
          .pipe(
            mergeMap(arbeid =>
                //utfør arbeid, registrer resultatet i ArbeidIntern instansen
                from(arbeid.arbeid(arbeid.id)).pipe(map(resultat => arbeid.utført(resultat))),
              // Sørger for at maks <antallParalleller> arbeider blir konsumert samtidig
              this.antallParalleller)
          )
          .subscribe(arbeid => {
              this.kødybdeSignal.update(dybde => dybde - 1);
              debug(`utført arbeid ${arbeid.id} (kødybde etter: ${this.kødybdeSignal}`);
          });

        return this;
    }

    get kødybde(): Signal<number> {
        return this.kødybdeSignal;
    }
}

export type Arbeid<T> = (arbeidId: string) => Promise<T>;

export interface PlanlagtArbeid<T> {
    readonly id: string;
    avslutt: (resultat: T) => void;
}

export interface PlanlagtAsynkrontArbeid<T> {
    readonly id: string;

    utført(): boolean;

    readonly resultat: Promise<T>
}

class ArbeidIntern<T> {

    private erUtført = false;
    private resultatSomPromise = promiseMedResolvere<T>();

    constructor(public readonly arbeid: Arbeid<T>, public readonly id = nyUuid()) {
    }

    utført(resultat: T) {
        this.erUtført = true;
        this.resultatSomPromise.resolve(resultat);
        return this;
    }

    somPlanlagtAsynkrontArbeid(): PlanlagtAsynkrontArbeid<T> {
        return {
            id: this.id,
            utført: () => this.erUtført,
            resultat: this.resultatSomPromise.promise
        }
    }
}
